import { z } from "zod";
import { workflowDryRunFixtureSchema } from "./workflow-dry-run.js";
import { workflowNodeTraceSchema } from "./workflow-trace.js";
import { workflowItemFailureSchema } from "./workflow-item-failure.js";
import { workflowDocumentAdapterDescriptorSchema } from "./workflow-document-adapters.js";
import { workflowSourceCapabilityDescriptorSchema } from "./workflow-plugin-sources.js";
import { workflowActivationReceiptSchema, workflowResourcePlanDiffSchema, workflowValidationEvidenceSchema } from "./workflow-storage.js";
import { durableWorkflowDefinitionSchema, workflowEffectSchema, workflowTriggerSchema, workflowDocumentIntakeSchema } from "./workflow-resources.js";
import { WORKFLOW_DOCUMENT_ADAPTERS } from "./workflow-document-adapters.js";
import { WORKFLOW_SOURCE_CAPABILITIES, workflowPluginSourcePayloadSchema } from "./workflow-plugin-sources.js";
import { workflowSourceQuerySchema } from "./worker-workflow-items.js";
import { workflowAnyDefinitionSchema, workflowDefinitionSchema, workflowJudgmentRequestSchema, workflowJudgmentResponseSchema, workflowPreviewSchema, workflowSchema, workflowStatusFilterSchema, } from "./workflows.js";
export const listTwinWorkflowsRequestSchema = z
    .object({
    sessionId: z.string().min(1).optional(),
    status: workflowStatusFilterSchema.optional(),
})
    .strict();
export const workflowSampleRequestSchema = z
    .object({
    version: z.number().int().positive(),
    fixture: workflowDryRunFixtureSchema.optional(),
    idempotencyKey: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[a-zA-Z0-9_-]+$/),
})
    .strict();
/** Scenario of the task run a "Test sample" creates; session timelines use it to list samples. */
export const WORKFLOW_SAMPLE_SCENARIO = "workflow-sample-v1";
export const workflowSampleResponseSchema = z.object({
    runId: z.string(),
    workflowId: z.string(),
    version: z.number().int().positive(),
    status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
    preview: workflowPreviewSchema.nullable(),
    error: z.string().nullable(),
    /** Live node traces from the run's latest attempt, ordered by startedAt. */
    nodes: z.array(workflowNodeTraceSchema).max(64),
});
export const createTwinWorkflowRequestSchema = z
    .object({
    sessionId: z.string().min(1),
    definition: workflowAnyDefinitionSchema,
})
    .strict();
export const updateTwinWorkflowRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    definition: workflowAnyDefinitionSchema,
})
    .strict();
export const validateTwinWorkflowRequestSchema = z
    .object({ definition: z.unknown() })
    .strict();
export const workflowValidationResponseSchema = z
    .object({
    valid: z.boolean(),
    definition: workflowAnyDefinitionSchema.optional(),
    /** Each issue's place in the definition as a JSON Pointer ("" for the whole), and the schema's issue code. */
    issues: z.array(z.object({ path: z.string(), message: z.string(), code: z.string().optional() }).strict()),
})
    .strict();
export const planTwinWorkflowRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
}).strict();
export const workflowResourcePlanResponseSchema = z.object({
    id: z.string().min(1), workflowId: z.string().min(1),
    workflowVersion: z.number().int().positive(), channelId: z.string().min(1),
    planHash: z.string().regex(/^[0-9a-f]{64}$/u),
    expiresAt: z.number().int().positive(),
    diff: workflowResourcePlanDiffSchema,
    validationEvidence: workflowValidationEvidenceSchema,
    executionPrincipalId: z.string().min(1),
    effects: z.array(workflowEffectSchema).max(64),
    triggers: z.array(workflowTriggerSchema).max(32),
    documentIntakes: z.array(workflowDocumentIntakeSchema).max(8).optional(),
}).strict();
export const applyTwinWorkflowPlanRequestSchema = z.object({
    planHash: z.string().regex(/^[0-9a-f]{64}$/u),
}).strict();
export const setTwinWorkflowActivationRequestSchema = z.object({
    expectedWorkflowVersion: z.number().int().positive(),
    status: z.enum(["active", "paused"]),
}).strict();
export const workflowActivationResponseSchema = z.object({
    id: z.string().min(1), workflowId: z.string().min(1),
    workflowVersion: z.number().int().positive(), channelId: z.string().min(1),
    status: z.enum(["paused", "active", "superseded"]),
    receipt: workflowActivationReceiptSchema,
}).strict();
export const retryTwinWorkflowItemRequestSchema = z.object({
    taskRunId: z.string().min(1), itemId: z.string().min(1),
}).strict();
export const retryTwinWorkflowItemResponseSchema = z.object({
    taskRunId: z.string().min(1), replayed: z.boolean(),
}).strict();
export const workflowOperationalQuerySchema = z.object({
    runId: z.string().min(1).max(200).optional(), itemId: z.string().min(1).max(200).optional(),
    attempt: z.coerce.number().int().positive().optional(),
    evidenceId: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
}).strict().refine(q => Boolean(q.runId) === Boolean(q.itemId) && (!q.attempt || Boolean(q.runId)) && (!q.evidenceId || Boolean(q.runId)), "Select a run and item together");
const workflowProcessState = {
    reviewState: z.enum(["waiting_review", "approved", "rejected", "expired", "invalidated"]).nullable().optional(),
    reviewProposalId: z.string().nullable().optional(),
    reviewBatchId: z.string().nullable().optional(),
    postingState: z.enum(["queued", "dispatching", "needs_reconciliation", "posted", "rejected", "blocked", "superseded"]).nullable().optional(),
    postingIntentId: z.string().nullable().optional(), postingReceipt: z.json().nullable().optional(), postingError: z.string().nullable().optional(),
    correctionState: z.enum(["pending", "queued", "blocked", "superseded"]).nullable().optional(),
    correctionRevision: z.string().nullable().optional(), correctionRunId: z.string().nullable().optional(),
    correctionError: z.string().nullable().optional(),
};
export const workflowRunTraceSchema = z.object({
    ...workflowProcessState,
    runId: z.string(), itemId: z.string(), channelId: z.string(), datasetId: z.string(), title: z.string().nullable(), attempt: z.number().int().nonnegative(),
    latestAttempt: z.number().int().nonnegative(), workflowVersion: z.number().int().positive(),
    definition: workflowAnyDefinitionSchema,
    itemStatus: z.enum(["pending", "running", "succeeded", "failed", "retry", "skipped"]),
    error: z.string().nullable(), nodes: z.array(workflowNodeTraceSchema).max(64),
    execution: z.object({ id: z.string(), evaluatedAt: z.number(), environment: z.record(z.string(), z.json()) }).nullable().optional(),
    evidence: z.object({ id: z.string(), value: z.json() }).nullable().optional(),
}).strict();
export const workflowOperationalStateResponseSchema = z.object({
    trace: workflowRunTraceSchema.nullable().optional(),
    workflowId: z.string().min(1),
    activation: z.object({
        id: z.string().min(1), workflowVersion: z.number().int().positive(),
        channelId: z.string().min(1), status: z.enum(["active", "paused", "superseded"]),
        approvedAt: z.number().int().positive(),
    }).strict().nullable(),
    triggers: z.array(z.object({
        key: z.string().min(1), enabled: z.boolean(), nextDueAt: z.number().int().nullable(),
        lastFiredAt: z.number().int().nullable(), lastErrorCode: z.string().nullable(),
        lastErrorAt: z.number().int().nullable(),
    }).strict()),
    /**
     * The workflow's latest run (a trigger, an upload, a re-queue or a retry; never a Test sample), in its reply's
     * words: "Processed 3 items: 2 done, 1 skipped." While it runs: "Running: 12 of 42 items done so far." Null before
     * its first run.
     */
    lastRun: z.object({ taskRunId: z.string().min(1), startedAt: z.number().int(),
        finishedAt: z.number().int().nullable(), summary: z.string().min(1) }).strict().nullable().optional(),
    intakeFailures: z.array(z.object({
        id: z.string(), sourceKey: z.string(), messageId: z.string(), attachmentKey: z.string(),
        status: z.enum(["failed", "missing"]), errorCode: z.string().nullable(),
        observedAt: z.number().int(),
    }).strict()).max(50).optional(),
    recentItems: z.array(z.object({
        ...workflowProcessState,
        itemId: z.string().min(1), taskRunId: z.string().min(1),
        channelId: z.string().min(1), datasetId: z.string().min(1), title: z.string().nullable(),
        /** `skipped`: the workflow does not handle the item (an upload of a type it does not read); errorDetail says why. */
        status: z.enum(["pending", "running", "succeeded", "failed", "retry", "skipped"]),
        inputRevision: z.string().min(1), errorCode: z.string().nullable(),
        errorDetail: z.string().nullable(), updatedAt: z.number().int().positive(),
        /** For a failed item: where it failed, in words, and how it can run again. */
        failure: workflowItemFailureSchema.nullable().optional(),
    }).strict()).max(50),
}).strict();
export const twinWorkflowResponseSchema = z
    .object({ workflow: workflowSchema })
    .strict();
export const restoreTwinWorkflowRequestSchema = z
    .object({
    version: z.number().int().positive(),
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const restoreTwinWorkflowResponseSchema = z
    .object({ version: z.number().int().positive() })
    .strict();
export const archiveTwinWorkflowRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const archiveTwinWorkflowResponseSchema = z
    .object({
    archived: z.literal(true),
    workflowId: z.string(),
})
    .strict();
export const workflowDefinitionHelpResponseSchema = z
    .object({
    schema: z.record(z.string(), z.unknown()),
    durableSchema: z.record(z.string(), z.unknown()).optional(),
    documentAdapters: z.array(workflowDocumentAdapterDescriptorSchema).optional(),
    sourceCapabilities: z.array(workflowSourceCapabilityDescriptorSchema),
    sourceRequestSchemas: z.object({ relational: z.record(z.string(), z.unknown()),
        plugin: z.record(z.string(), z.unknown()) }).strict(),
    judgeRequestSchema: z.record(z.string(), z.unknown()),
    /** What a judge step's next step receives: the answer to each question, keyed by the question's id. */
    judgeResponseSchema: z.record(z.string(), z.unknown()).optional(),
    execution: z.object({
        functionArgument: z.literal("inputs"),
        sourceReturnShape: z.literal("{tool, arguments}"),
        allowedImports: z.array(z.string()),
        judgeReturnShape: z.literal("{state, questions}"),
    }),
})
    .strict();
/** Both definition schemas, the source adapters and the execution conventions, for authors and the authoring agent. */
export function workflowDefinitionHelp() {
    return workflowDefinitionHelpResponseSchema.parse({
        schema: z.toJSONSchema(workflowDefinitionSchema),
        durableSchema: z.toJSONSchema(durableWorkflowDefinitionSchema),
        documentAdapters: WORKFLOW_DOCUMENT_ADAPTERS,
        sourceCapabilities: WORKFLOW_SOURCE_CAPABILITIES,
        sourceRequestSchemas: { relational: z.toJSONSchema(workflowSourceQuerySchema),
            plugin: z.toJSONSchema(workflowPluginSourcePayloadSchema) },
        judgeRequestSchema: z.toJSONSchema(workflowJudgmentRequestSchema),
        judgeResponseSchema: z.toJSONSchema(workflowJudgmentResponseSchema),
        execution: {
            functionArgument: "inputs", sourceReturnShape: "{tool, arguments}",
            allowedImports: ["json", "math", "decimal", "datetime", "statistics", "re"],
            judgeReturnShape: "{state, questions}",
        },
    });
}
export const workflowSourceSchema = z
    .object({
    pluginSlug: z.string(),
    pluginVersion: z.string(),
    reviewedDefinitionHash: z.string().regex(/^[0-9a-f]{64}$/u),
    name: z.string(),
    label: z.string(),
    description: z.string(),
    inputSchema: z.record(z.string(), z.unknown()),
})
    .strict();
export const workflowSourcesResponseSchema = z
    .object({
    sources: z.array(workflowSourceSchema),
})
    .strict();
export const TWIN_WORKFLOW_OPERATIONS = {
    listTwinWorkflows: {
        operationId: "twin.workflows.list",
        method: "GET",
        path: "/api/twin/workflows",
        requiredScope: "twin.read",
    },
    listTwinWorkflowVersions: {
        operationId: "twin.workflows.versions",
        method: "GET",
        path: "/api/twin/workflows/versions",
        requiredScope: "twin.read",
    },
    getTwinWorkflowSchema: {
        operationId: "twin.workflows.schema",
        method: "GET",
        path: "/api/twin/workflows/schema",
        requiredScope: "twin.read",
    },
    listTwinWorkflowSources: {
        operationId: "twin.workflows.sources",
        method: "GET",
        path: "/api/twin/workflows/sources",
        requiredScope: "twin.read",
    },
    validateTwinWorkflow: {
        operationId: "twin.workflows.validate",
        method: "POST",
        path: "/api/twin/workflows/validate",
        requiredScope: "twin.read",
    },
    planTwinWorkflow: {
        operationId: "twin.workflows.plan",
        method: "POST",
        path: "/api/twin/workflows/{workflowId}/plans",
        requiredScope: "twin.write",
    },
    applyTwinWorkflowPlan: {
        operationId: "twin.workflows.plan.apply",
        method: "POST",
        path: "/api/twin/workflows/{workflowId}/plans/{planId}/apply",
        requiredScope: "twin.write",
    },
    setTwinWorkflowActivation: {
        operationId: "twin.workflows.activation.state",
        method: "POST",
        path: "/api/twin/workflows/{workflowId}/activations/{activationId}/state",
        requiredScope: "twin.write",
    },
    retryTwinWorkflowItem: {
        operationId: "twin.workflows.item.retry",
        method: "POST",
        path: "/api/twin/workflows/{workflowId}/items/retry",
        requiredScope: "twin.write",
    },
    getTwinWorkflowOperationalState: {
        operationId: "twin.workflows.operations.get",
        method: "GET",
        path: "/api/twin/workflows/{workflowId}/operations",
        requiredScope: "twin.read",
    },
    createTwinWorkflow: {
        operationId: "twin.workflows.create",
        method: "POST",
        path: "/api/twin/workflows",
        requiredScope: "twin.write",
    },
    getTwinWorkflow: {
        operationId: "twin.workflows.get",
        method: "GET",
        path: "/api/twin/workflows/{workflowId}",
        requiredScope: "twin.read",
    },
    updateTwinWorkflow: {
        operationId: "twin.workflows.update",
        method: "PUT",
        path: "/api/twin/workflows/{workflowId}",
        requiredScope: "twin.write",
    },
    archiveTwinWorkflow: {
        operationId: "twin.workflows.archive",
        method: "DELETE",
        path: "/api/twin/workflows/{workflowId}",
        requiredScope: "twin.write",
    },
    restoreTwinWorkflow: {
        operationId: "twin.workflows.restore",
        method: "POST",
        path: "/api/twin/workflows/{workflowId}/restore",
        requiredScope: "twin.write",
    },
    sampleTwinWorkflow: {
        operationId: "twin.workflows.sample",
        method: "POST",
        path: "/api/twin/workflows/{workflowId}/samples",
        requiredScope: "twin.write",
    },
    getTwinWorkflowSample: {
        operationId: "twin.workflows.sample.get",
        method: "GET",
        path: "/api/twin/workflows/{workflowId}/samples/{runId}",
        requiredScope: "twin.read",
    },
};
